import * as amplitude from "@amplitude/analytics-browser";
import { useHandler } from "@redotech/react-util/hook";
import type { FulfillmentGroupData } from "@redotech/redo-fulfillment-group-common/fulfillment-group";
import {
  OutboundParcel,
  OutboundParcelSchema,
  Parcel,
} from "@redotech/redo-fulfillment-parcel-common/parcel";
import { Preset } from "@redotech/redo-fulfillment-parcel-common/presets";
import { WeightChangeSource } from "@redotech/redo-merchant-app-common/components/weight-widget/total-weight-widget";
import { FulfillmentGroupUpdate } from "@redotech/redo-merchant-fulfillment-rpc/schema/update-fulfillment-groups";

type ParcelFulfillmentGroup = Pick<
  FulfillmentGroupData,
  "_id" | "selectedParcels"
>;

export const useParcelActions = ({
  fulfillmentGroups,
  parcelValues,
  updateRows,
  setSelectedPreset,
}: {
  fulfillmentGroups: readonly ParcelFulfillmentGroup[];
  parcelValues: readonly Parcel[];
  updateRows: (updates: FulfillmentGroupUpdate[]) => void;
  setSelectedPreset: (preset: Preset | undefined) => void;
}): {
  onParcelChange: (
    parcel: Partial<OutboundParcel>,
    index: number,
    source?: WeightChangeSource,
  ) => void;
  onRemoveParcel: (index: number) => void;
  onParcelAdd: (parcel: Parcel) => void;
  onAllocationsFlush: (
    allocations: Map<
      number,
      { externalId: string; sku: string; quantity: number }[]
    >,
  ) => void;
} => {
  const defaultParcel = parcelValues.find((parcel) => parcel.default);

  const onParcelChange = useHandler(
    (
      parcelChange: Partial<OutboundParcel>,
      index: number,
      source?: WeightChangeSource,
    ) => {
      const fulfillmentGroupUpdates: FulfillmentGroupUpdate[] =
        fulfillmentGroups.map((group) => {
          const currentParcel =
            group.selectedParcels?.[index] ??
            (group.selectedParcels == null && index === 0 && defaultParcel
              ? OutboundParcelSchema.parse(defaultParcel)
              : undefined);

          if (!currentParcel) {
            return { fulfillmentGroupId: group._id };
          }

          // Ensure nested fields are not overwritten. Customs are no longer
          // a parcel-level concern — they are stored on the group and edited
          // via dedicated customs UI flows that write top-level group fields.
          const updatedParcelCandidate = {
            ...currentParcel,
            ...parcelChange,
            ...(parcelChange.insurance
              ? {
                  insurance: {
                    ...currentParcel.insurance,
                    ...parcelChange.insurance,
                  },
                }
              : {}),
          };

          let updatedParcel: OutboundParcel;
          try {
            updatedParcel = OutboundParcelSchema.parse(updatedParcelCandidate);
          } catch (error) {
            console.error("Invalid parcel update", {
              updatedParcel: updatedParcelCandidate,
              error,
            });
            return { fulfillmentGroupId: group._id };
          }

          return {
            fulfillmentGroupId: group._id,
            source,
            selectedParcels: group.selectedParcels?.map((parcel, i) =>
              i === index ? updatedParcel : parcel,
            ) ?? [updatedParcel],
          };
        });

      updateRows(fulfillmentGroupUpdates);
      setSelectedPreset(undefined);
    },
  );

  const onRemoveParcel = useHandler((index: number) => {
    const firstGroup = fulfillmentGroups[0];
    amplitude.logEvent("oms-shipment-parcel-removed", {
      ...(firstGroup ? { fulfillmentGroupId: firstGroup._id } : {}),
      fulfillmentGroupCount: fulfillmentGroups.length,
      parcelCount: Math.max(0, (firstGroup?.selectedParcels?.length ?? 0) - 1),
    });
    updateRows(
      fulfillmentGroups.map((group) => ({
        fulfillmentGroupId: group._id,
        selectedParcels: group.selectedParcels?.filter((_, i) => i !== index),
      })),
    );
  });

  const onParcelAdd = useHandler((parcel: Parcel) => {
    const firstGroup = fulfillmentGroups[0];
    amplitude.logEvent("oms-shipment-parcel-added", {
      ...(firstGroup ? { fulfillmentGroupId: firstGroup._id } : {}),
      fulfillmentGroupCount: fulfillmentGroups.length,
      parcelCount: (firstGroup?.selectedParcels?.length ?? 0) + 1,
    });
    updateRows(
      fulfillmentGroups.map((group) => ({
        fulfillmentGroupId: group._id,
        selectedParcels: [...(group.selectedParcels ?? []), parcel],
      })),
    );
  });

  const onAllocationsFlush = useHandler(
    (
      allocations: Map<
        number,
        { externalId: string; sku: string; quantity: number }[]
      >,
    ) => {
      updateRows(
        fulfillmentGroups.map((group) => ({
          fulfillmentGroupId: group._id,
          selectedParcels: (group.selectedParcels ?? []).map((parcel, i) => {
            const draft = allocations.get(i);
            if (draft === undefined) {
              return parcel;
            }
            return { ...parcel, itemAllocations: draft };
          }),
        })),
      );
    },
  );

  return { onParcelChange, onRemoveParcel, onParcelAdd, onAllocationsFlush };
};
